import { useEffect, useState } from 'react'
import { CheckIcon, ChevronRightIcon, TrashIcon } from '../components/icons'
import { JobIcon } from '../components/JobIcon'
import { formatDuration, formatMoney, normalizePrice } from '../lib/format'
import {
  PRICE_FOR_YOUR_CAR,
  priceStateOf,
  unconfirmedPriceLabel,
  type VehiclePricedService,
} from '../lib/vehiclePricing'
import { hasOptionsPanel, matchJobIcon, primaryService, type JobType } from '../lib/jobTypes'
import type { BasketItem } from '../lib/services'
import type { UpsellPrompt } from '../lib/upsells'
import type { WidgetBootstrapResponse } from '../types/PublicBookingChannelTypes'
import type { MotPrompt } from './ExtrasStep'

export interface BuildStepProps {
  jobTypes: JobType[]
  basket: BasketItem[]
  onToggleService: (jobType: JobType, service: WidgetBootstrapResponse.ServiceItem) => void
  showPrices: boolean
  loading: boolean
  /** Set once we know the vehicle, so the heading can name it. */
  vehicleLabel: string | null
  /** Set when the vehicle's MOT is worth leading with. */
  motPrompt: MotPrompt | null
  /** Reason-led suggestions, e.g. alignment after new tyres. */
  upsellPrompts: UpsellPrompt[]
  /**
   * The garage's advertised "tyres from" price, fully fitted. Shown on the tyre
   * card, which otherwise has no honest number to lead with until a supplier feed
   * can price the tyres themselves.
   */
  tyreFromPrice?: number | null
  /**
   * A category to show open when the step first appears, used when a booking
   * link names a kind of work that still holds a choice.
   */
  initialExpandedKey?: string | null
}

function describeMotUrgency(daysRemaining: number): string {
  if (daysRemaining < 0) return 'Your MOT has expired'
  if (daysRemaining === 0) return 'Your MOT expires today'
  if (daysRemaining <= 31) return `Your MOT expires in ${daysRemaining} days`
  return 'Your MOT is due soon'
}

/**
 * The single surface where a booking is built.
 *
 * This replaces three screens — pick a category, then pick a service, then get
 * offered extras — with one. Those three existed because a category *might* hold
 * several services, so everyone paid for the possibility: choose "MOT", get
 * marched to another screen to confirm you meant MOT.
 *
 * Here a category is a card. If there is one thing to book, tapping the card
 * books it. If there is a real choice, the card opens in place and the customer
 * chooses without going anywhere — the progress bar does not move, because no
 * step happened. Adding a second job is the same gesture as the first, which is
 * what makes a two-job booking feel like one decision instead of a queue.
 *
 * The cross-sell lives here too rather than on a screen of its own: everything
 * the garage sells is already on this list, so "anything else?" is answered by
 * scrolling, and the prompts simply point at the answer.
 */
export function BuildStep({
  jobTypes,
  basket,
  onToggleService,
  showPrices,
  loading,
  vehicleLabel,
  motPrompt,
  upsellPrompts,
  tyreFromPrice,
  initialExpandedKey = null,
}: BuildStepProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(initialExpandedKey)
  const [revealedKey, setRevealedKey] = useState<string | null>(null)

  // Opening a card closes any conditional list from a previous one, so "more
  // options" never stays open on a card the customer has moved past.
  useEffect(() => {
    if (revealedKey && revealedKey !== expandedKey) setRevealedKey(null)
  }, [expandedKey, revealedKey])

  if (loading) {
    return (
      <div className="ac-stack" aria-busy="true">
        <span className="ac-sr-only">Loading services</span>
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="ac-skeleton ac-skeleton--card" />
        ))}
      </div>
    )
  }

  if (jobTypes.length === 0) {
    return (
      <div className="ac-empty">
        <p className="ac-prose">This garage hasn&apos;t listed any services yet.</p>
      </div>
    )
  }

  const chosenIn = (jobType: JobType) =>
    jobType.services.filter((service) => basket.some((item) => item.serviceId === service.id))

  const handleCardTap = (jobType: JobType) => {
    const chosen = chosenIn(jobType)
    const main = primaryService(jobType)

    // One thing to book (or the one the garage nominated): the card is the
    // button. Tapping again takes it back out, so nothing needs a separate
    // remove control at this size. A real choice only opens the card: nothing
    // goes in the basket until the customer picks an option (ACA-100).
    if (main && chosen.length === 0) {
      onToggleService(jobType, main)
      return
    }
    if (main && chosen.length > 0 && !hasOptionsPanel(jobType)) {
      onToggleService(jobType, main)
      return
    }

    setExpandedKey((current) => (current === jobType.key ? null : jobType.key))
  }

  return (
    <div className="ac-stack">
      <div className="ac-stack ac-stack--tight">
        <p className="ac-eyebrow">{vehicleLabel ?? 'Book online'}</p>
        <h3 className="ac-heading ac-heading--display">
          {vehicleLabel ? 'What does it need?' : 'What does your car need?'}
        </h3>
        <p className="ac-prose">Add as much as you like. We&apos;ll do it all in one visit.</p>
      </div>

      {motPrompt ? (
        <button
          type="button"
          className="ac-promo"
          onClick={() =>
            motPrompt.service
              ? onToggleService(motPrompt.jobType, motPrompt.service)
              : handleCardTap(motPrompt.jobType)
          }
          aria-label={`Add ${motPrompt.service?.name ?? motPrompt.jobType.label}, ${describeMotUrgency(motPrompt.daysRemaining)}`}
        >
          <span className="ac-promo__icon">
            <JobIcon name="mot" size={26} />
          </span>
          <span className="ac-promo__main">
            <span className="ac-promo__title">{describeMotUrgency(motPrompt.daysRemaining)}</span>
            <span className="ac-promo__detail">Expires {motPrompt.expiryLabel} · add it to this visit</span>
          </span>
          <span className="ac-promo__action">
            {showPrices && motPrompt.fromPrice != null
              ? formatMoney(motPrompt.fromPrice)
              : 'Add'}
          </span>
        </button>
      ) : null}

      {upsellPrompts.map((prompt) => (
        <button
          key={prompt.jobType.key}
          type="button"
          className="ac-promo ac-promo--quiet"
          onClick={() => handleCardTap(prompt.jobType)}
        >
          <span className="ac-promo__icon">
            <JobIcon name={prompt.icon} size={24} />
          </span>
          <span className="ac-promo__main">
            <span className="ac-promo__title">{prompt.title}</span>
            <span className="ac-promo__detail">{prompt.detail}</span>
          </span>
          <span className="ac-promo__action">
            {showPrices && prompt.jobType.fromPrice != null
              ? formatMoney(prompt.jobType.fromPrice)
              : showPrices && prompt.jobType.freeOption
                ? 'Free'
                : 'Add'}
          </span>
        </button>
      ))}

      <ul className="ac-cards">
        {jobTypes
          .filter((jobType) => !upsellPrompts.some((prompt) => prompt.jobType.key === jobType.key))
          .map((jobType) => {
            const chosen = chosenIn(jobType)
            const selected = chosen.length > 0
            const expanded = expandedKey === jobType.key
            const revealed = revealedKey === jobType.key
            const choosable = jobType.bookable.length > 1
            // A single test with a retest beside it still opens, so the retest
            // can be chosen on purpose without ever being added for everyone.
            const panel = hasOptionsPanel(jobType)
            const conditional = jobType.services.filter(
              (service) => !jobType.bookable.includes(service)
            )
            const panelId = `ac-card-${jobType.key.replace(/\s+/g, '-')}`
            const options = revealed ? jobType.services : jobType.bookable

            // Tyres are a special case and stay one: whatever is priced in that
            // category — fitting, a puncture repair — is not the price of a tyre,
            // and a customer reading "Tyres from £22" is reading the fitting
            // charge. Only the garage's advertised entry price may appear here,
            // and without one the card asks rather than tells.
            const isTyres = matchJobIcon(jobType.key) === 'tyres'
            const advertised = isTyres ? tyreFromPrice : null
            const quoteOnly = jobType.quoteOnly || (isTyres && advertised == null)

            return (
              <li
                key={jobType.key}
                className={`ac-card${selected ? ' ac-card--selected' : ''}${expanded ? ' ac-card--open' : ''}`}
              >
                <button
                  type="button"
                  className="ac-card__head"
                  onClick={() => handleCardTap(jobType)}
                  aria-expanded={panel ? expanded : undefined}
                  aria-controls={panel ? panelId : undefined}
                  aria-pressed={panel ? undefined : selected}
                >
                  <span className="ac-card__icon" aria-hidden="true">
                    {selected && !choosable ? (
                      <CheckIcon size={20} />
                    ) : (
                      <JobIcon name={jobType.icon} size={22} />
                    )}
                  </span>

                  <span className="ac-card__main">
                    <span className="ac-card__label">{jobType.label}</span>

                    {selected ? (
                      <span className="ac-card__chosen">
                        {chosen.map((service) => service.name).join(' · ')}
                      </span>
                    ) : choosable ? (
                      <span className="ac-card__desc">
                        {jobType.bookable.length} options to choose from
                      </span>
                    ) : jobType.bookable[0]?.description ? (
                      <span className="ac-card__desc">{jobType.bookable[0].description}</span>
                    ) : null}
                  </span>

                  <span className="ac-card__meta">
                    {showPrices && advertised != null ? (
                      <span className="ac-card__price">
                        from <strong>{formatMoney(advertised)}</strong>
                      </span>
                    ) : showPrices && !quoteOnly && jobType.fromPrice != null ? (
                      <span className="ac-card__price">
                        {jobType.singlePrice ? null : 'from '}
                        <strong>{formatMoney(jobType.fromPrice)}</strong>
                      </span>
                    ) : showPrices && !quoteOnly && jobType.freeOption ? (
                      <span className="ac-card__price">
                        <strong>Free</strong>
                      </span>
                    ) : showPrices && quoteOnly && jobType.headlinePriceState !== 'fixed' ? (
                      <span className="ac-card__price ac-card__price--quote">
                        {unconfirmedPriceLabel(jobType.headlinePriceState)}
                      </span>
                    ) : showPrices && quoteOnly ? (
                      <span className="ac-card__price ac-card__price--quote">
                        <strong>Get a price</strong>
                      </span>
                    ) : null}

                    {showPrices && advertised != null ? (
                      <span className="ac-card__free">fitted &amp; balanced</span>
                    ) : showPrices && !quoteOnly && jobType.fromPrice != null && jobType.freeOption ? (
                      <span className="ac-card__free">{jobType.freeOption}</span>
                    ) : null}

                    {panel ? (
                      <span
                        className={`ac-card__chevron${expanded ? ' ac-card__chevron--open' : ''}`}
                        aria-hidden="true"
                      >
                        <ChevronRightIcon size={18} />
                      </span>
                    ) : null}
                  </span>
                </button>

                {panel && expanded ? (
                  <div className="ac-card__panel" id={panelId}>
                    <ul className="ac-list">
                      {options.map((service) => {
                        const inBasket = basket.some((item) => item.serviceId === service.id)
                        const price = normalizePrice(service.price)
                        const priceState = priceStateOf(service)
                        const duration = formatDuration(service.durationMinutes)

                        return (
                          <li key={service.id}>
                            <button
                              type="button"
                              className={`ac-option${inBasket ? ' ac-option--selected' : ''}`}
                              aria-pressed={inBasket}
                              onClick={() => onToggleService(jobType, service)}
                            >
                              <span className="ac-option__check" aria-hidden="true">
                                <CheckIcon size={15} />
                              </span>

                              <span className="ac-option__main">
                                <span className="ac-option__name">{service.name}</span>
                                {service.description ? (
                                  <span className="ac-option__desc">{service.description}</span>
                                ) : null}
                                {service.subheadings?.length ? (
                                  <span className="ac-option__tags">
                                    {service.subheadings.map((sub) => (
                                      <span key={sub} className="ac-tag">
                                        {sub}
                                      </span>
                                    ))}
                                  </span>
                                ) : null}
                              </span>

                              <span className="ac-option__meta">
                                {showPrices && priceState !== 'fixed' ? (
                                  <span className="ac-option__price">
                                    {unconfirmedPriceLabel(priceState)}
                                  </span>
                                ) : showPrices && price != null ? (
                                  <span className="ac-option__price">
                                    {price > 0 ? formatMoney(price) : 'Free'}
                                  </span>
                                ) : null}
                                {showPrices &&
                                price != null &&
                                (service as VehiclePricedService).priceBasis === 'FOR_YOUR_CAR' ? (
                                  <span className="ac-option__duration">{PRICE_FOR_YOUR_CAR}</span>
                                ) : null}
                                {duration ? (
                                  <span className="ac-option__duration">{duration}</span>
                                ) : null}
                              </span>
                            </button>
                          </li>
                        )
                      })}
                    </ul>

                    {/* Conditional work — an MOT retest — is real and bookable, but
                        only for someone who already knows they need it. It must not
                        stand between everybody else and the thing they came for. */}
                    {conditional.length > 0 && !revealed ? (
                      <button
                        type="button"
                        className="ac-card__more"
                        onClick={() => setRevealedKey(jobType.key)}
                      >
                        More options
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {/* A single-service card that is in the basket gets an explicit way
                    out, because tapping the card again is not obviously "remove". */}
                {selected && !choosable ? (
                  <button
                    type="button"
                    className="ac-card__remove"
                    onClick={() => onToggleService(jobType, chosen[0])}
                    aria-label={`Remove ${chosen[0].name}`}
                  >
                    <TrashIcon size={15} />
                  </button>
                ) : null}
              </li>
            )
          })}
      </ul>
    </div>
  )
}
